import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, CheckCircle2, Compass, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tools } from "@/data/publicFoundation";
import { getCalculatorGuide, getCalculatorToolContent } from "./calculatorTranslations";
import { calculatorHubCopy, getCalculatorHubCopy } from "./calculatorHubCopy";
import CalculatorHubSeoFaq from "./CalculatorHubSeoFaq";

export default function CalculatorHub({ lang = "en", homeCopy = {}, publicCopy = {} }) {
  const routeLang = window.location.pathname.split("/").filter(Boolean)[0];
  const activeLang = calculatorHubCopy[routeLang] ? routeLang : lang;
  const prefix = `/${activeLang}`;
  const copy = getCalculatorHubCopy(activeLang);

  return (
    <section id="calculator-tools" className="mt-6 scroll-mt-24 space-y-6">
      <div className="relative overflow-hidden rounded-[1.5rem] border border-border bg-ocean p-4 text-white shadow-2xl shadow-ocean/20 md:p-7">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_15%,rgba(28,167,166,.46),transparent_26rem),radial-gradient(circle_at_92%_80%,rgba(255,255,255,.14),transparent_22rem)]" />
        <div className="relative grid gap-5 lg:grid-cols-[1fr_18rem] lg:items-end">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/12 px-3 py-1.5 text-xs font-black text-cyan-50"><Calculator className="h-3.5 w-3.5" /> {copy.eyebrow}</p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black leading-tight !text-white md:text-5xl">{copy.title}</h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-cyan-50/86">{copy.intro}</p>
          </div>
          <div className="rounded-[1.25rem] border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
            {copy.checklist.map((item) => (
              <div key={item} className="flex items-center gap-2.5 border-b border-white/10 py-2.5 text-sm last:border-0"><CheckCircle2 className="h-4 w-4 text-cyan-200" /><span className="font-bold">{item}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {tools.map((tool) => {
          const translated = { ...(homeCopy.tools?.[tool.slug] || {}), ...getCalculatorToolContent(tool.slug, activeLang) };
          const guide = getCalculatorGuide(tool.slug, activeLang);
          return (
            <article key={tool.slug} className="group flex min-h-full flex-col rounded-[2rem] border border-border bg-card p-6 shadow-xl shadow-ocean/5 transition hover:-translate-y-1 hover:border-primary/50 hover:shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary"><Compass className="h-6 w-6" /></div>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-black text-muted-foreground">{copy.calculator}</span>
              </div>
              <h3 className="mt-5 text-2xl font-black">{translated.title || tool.title}</h3>
              <p className="mt-3 flex-1 leading-7 text-muted-foreground">{translated.description || tool.description}</p>
              <div className="mt-5 rounded-2xl bg-muted/45 p-4 text-sm leading-6 text-muted-foreground">{guide.summary}</div>
              <Button asChild className="mt-5 w-full rounded-2xl">
                <Link to={`${prefix}/sailing-calculators/${tool.slug}`}>{publicCopy.common?.openTool || "Open tool"} <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </article>
          );
        })}
      </div>

      <div className="rounded-[2rem] border border-primary/20 bg-card p-6 shadow-xl shadow-ocean/5 md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.disciplineEyebrow}</p>
            <h2 className="mt-2 text-3xl font-black">{copy.disciplineTitle}</h2>
            <p className="mt-3 max-w-3xl leading-7 text-muted-foreground">{copy.disciplineText}</p>
          </div>
          <ShieldCheck className="hidden h-16 w-16 shrink-0 text-primary md:block" />
        </div>
      </div>

      <CalculatorHubSeoFaq lang={activeLang} />
    </section>
  );
}